import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signInWithPopup,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, Timestamp, onSnapshot } from 'firebase/firestore';
import { auth, googleProvider, db } from '../config/firebase';
import {
  GOD_MODE_EMAILS,
  isAdmin as isAdminCheck,
  isPM as isPMCheck,
  isDemo as isDemoCheck,
  canWrite as canWriteCheck,
  determineNewUserRole,
} from '../utils/authHelpers';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create or update user profile in Firestore
  const ensureUserProfile = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      setUserProfile(null);
      return null;
    }

    setProfileLoading(true);
    try {
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        // User exists - update lastLoginAt and sync any changed auth fields
        const existingData = userDoc.data();
        const updates = {
          lastLoginAt: Timestamp.now(),
        };

        // Sync display name and photo if changed in Google
        if (firebaseUser.displayName && firebaseUser.displayName !== existingData.displayName) {
          updates.displayName = firebaseUser.displayName;
        }
        if (firebaseUser.photoURL && firebaseUser.photoURL !== existingData.photoUrl) {
          updates.photoUrl = firebaseUser.photoURL;
        }

        await updateDoc(userDocRef, updates);

        const profile = { ...existingData, ...updates, uid: firebaseUser.uid };
        setUserProfile(profile);
        return profile;
      } else {
        // New user - create profile
        const newProfile = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || '',
          phone: null,
          photoUrl: firebaseUser.photoURL || null,
          role: determineNewUserRole(firebaseUser.email),
          status: 'active',
          assignedCards: [],
          createdAt: Timestamp.now(),
          createdBy: 'self',
          updatedAt: Timestamp.now(),
          lastLoginAt: Timestamp.now(),
          hireDate: null,
          emergencyContact: null,
        };

        await setDoc(userDocRef, newProfile);
        setUserProfile(newProfile);
        return newProfile;
      }
    } catch (err) {
      console.error('Error ensuring user profile:', err);
      setError(err.message);
      return null;
    } finally {
      setProfileLoading(false);
    }
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        await ensureUserProfile(firebaseUser);
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [ensureUserProfile]);

  // Subscribe to user profile updates in real-time
  useEffect(() => {
    if (!user) return;

    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        setUserProfile({ uid: user.uid, ...doc.data() });
      }
    }, (err) => {
      console.error('Error listening to user profile:', err);
    });

    return () => unsubscribe();
  }, [user]);

  const signInWithGoogle = async () => {
    try {
      setError(null);
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
      setUserProfile(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Update user profile (for self-service profile editing)
  const updateUserProfile = useCallback(async (updates) => {
    if (!user) throw new Error('Not authenticated');

    const userDocRef = doc(db, 'users', user.uid);
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    await updateDoc(userDocRef, updateData);
    // Local state will be updated by the onSnapshot listener
  }, [user]);

  // Check if current user has a specific role
  const hasRole = useCallback((role) => {
    if (!userProfile) return false;
    if (Array.isArray(role)) {
      return role.includes(userProfile.role);
    }
    return userProfile.role === role;
  }, [userProfile]);

  // Check if user is admin (includes GOD_MODE)
  const isAdmin = useCallback(() => isAdminCheck(userProfile), [userProfile]);

  // Check if user is PM or higher
  const isPM = useCallback(() => isPMCheck(userProfile), [userProfile]);

  // Check if user is demo (read-only access)
  const isDemo = useCallback(() => isDemoCheck(userProfile), [userProfile]);

  // Check if user can write/edit (not demo)
  const canWrite = useCallback(() => canWriteCheck(userProfile), [userProfile]);

  const value = {
    // Firebase Auth user (raw)
    user,
    // Firestore user profile (enhanced)
    userProfile,
    loading,
    profileLoading,
    error,
    signInWithGoogle,
    logout,
    isAuthenticated: !!user,
    // Profile management
    updateUserProfile,
    // Role checks
    hasRole,
    isAdmin,
    isPM,
    isDemo,
    canWrite,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
